<template lang="pug">
div
  AdvancedBatchOptionsModal(
    :visible="showAdvancedModal"
    @close="showAdvancedModal = false"
    @change="$emit('change')"
  )
  section.batch-options(v-if="storyBatchOptionsStore.anySelectedStories")
    .panel.panel-default
      .panel-heading
        .flex-between
          strong Bulk Edit
          HelpPopover(title="Bulk Edit" :content="bulkEditHelp")
      .panel-body
        .row
          .col-md-3
            .form-group
              .flex-between
                label Pub date
                HelpPopover(title="Pub date" :content="pubDateHelp")
              DateField(
                input-class="form-control"
                :disabled-dates="{to: new Date()}"
                placeholder="Don't Change"
                v-model="storyBatchOptionsStore.pubDate"
              )

          .col-md-3
            .form-group
              .flex-between
                label Owner
                HelpPopover(title="Owner" :content="ownerHelp")
              SelectInput(
                v-model="storyBatchOptionsStore.userId"
                blank-option-name="Don't Change"
                :options="usersStore.allAsObject"
              )

          .col-md-2
            .form-group
              .flex-between
                label Print section
                HelpPopover(title="Print section" :content="printSectionHelp")
              SelectInput(
                v-model="storyBatchOptionsStore.sectionId"
                blank-option-name="Don't Change"
                :options="sectionOptions"
              )

          .col-md-2
            .form-group
              .flex-between
                label Status
                HelpPopover(title="Status" :content="statusHelp")
              SelectInput(
                v-model="storyBatchOptionsStore.squeueId"
                blank-option-name="Don't Change"
                :options="squeueOptions"
              )

          .col-md-2
            .form-group
              .flex-between
                label Web category
                HelpPopover(title="Web category" :content="webCategoryHelp")
              SelectInput(
                v-model="storyBatchOptionsStore.webCategoryId"
                blank-option-name="Don't Change"
                :options="webCategoriesStore.allAsObject"
              )

        .row
          .col-md-12.text-right
            button.btn.btn-default(
              type="button"
              @click="showAdvancedModal = true"
            )
              | Advanced Editing Options
            |
            |
            button.btn.btn-primary(
              type="button"
              :disabled="!storyBatchOptionsStore.anyChanges"
              v-on:click="submit()"
              v-text="buttonTitle('Update')"
            )

    .panel.panel-default
      .panel-heading
        .flex-between
          strong Download / Export
          HelpPopover(title="Download / Export" :content="downloadExportHelp")
      .panel-body
        a.btn.btn-default(:href="downloadLink")
          i.fa.fa-cloud-download
          |
          | Download
        |
        |
        .btn-group(v-if="hasBatchExportFormats")
          button.btn.btn-default.dropdown-toggle(
            type="button"
            data-toggle="dropdown"
          )
            i.fa.fa-file-text
            |
            | Download Merged
            |
            i.fa.fa-caret-down
          ul.dropdown-menu
            li(v-for="format in exportFormatsStore.forBatchExport" :key="format.id")
              a(@click="downloadBatchExport(format.id)") {{ format.name }}
        |
        |
        a.btn.btn-default(v-on:click="openCommunityQPublishModal()")
          i.fa.fa-cloud-upload
          |
          | Export to Web
</template>

<script>
  import DateField from '@/components/shared/date_field.vue'
  import httpClient from '@/api/httpClient'
  import { mapGetters } from 'vuex';
  import { mapStores } from 'pinia'
  import { useCommunityqPublishingStore } from '@/stores/communityq_publishing'
  import { useExportFormatsStore } from '@/stores/export_formats'
  import { useWebCategoriesStore } from '@/stores/web_categories'
  import { useStoryBatchOptionsStore } from '@/stores/story_batch_options'
  import { useSectionsStore } from '@/stores/sections'
  import { useUsersStore } from '@/stores/users'
  import HelpPopover from '@/components/shared/help_popover.vue'
  import SelectInput from '@/components/shared/select_input'
  import AdvancedBatchOptionsModal from './advanced_batch_options_modal.vue'

  export default {
    components: {
      AdvancedBatchOptionsModal,
      DateField,
      HelpPopover,
      SelectInput
    },

    data() {
      return {
        showAdvancedModal: false,

        bulkEditHelp: `Use these fields to update multiple stories at once. Select a value in any field to apply that change to all selected stories. Fields left as "Don't Change" will not be modified.

Click "Advanced Editing Options" for additional bulk editing capabilities. When you're ready, click the Update button to apply your changes.`,

        pubDateHelp: `Set the publication date for all selected stories. This is the date the stories are expected to be published.

Leave as "Don't Change" to keep each story's current pub date.`,

        ownerHelp: `Reassign all selected stories to a different user. The owner is typically the editor or reporter responsible for the story.

Leave as "Don't Change" to keep each story's current owner.`,

        printSectionHelp: `Assign all selected stories to a print section. Sections represent the different parts of your publication, such as News, Sports, or Opinion.

Leave as "Don't Change" to keep each story's current section.`,

        statusHelp: `Change the workflow status for all selected stories. Statuses track where stories are in the editorial process, such as Draft, Ready for Edit, or Ready to Publish.

Leave as "Don't Change" to keep each story's current status.`,

        webCategoryHelp: `Assign all selected stories to a web category. Web categories control where stories appear on your website.

Leave as "Don't Change" to keep each story's current web category.`,

        downloadExportHelp: `These actions let you download or export the selected stories without modifying them.

Download downloads the original content files for the selected stories as a ZIP archive.

Download Merged combines the selected stories into a single file using one of your configured export formats, such as an InDesign template.

Export to Web publishes the selected stories to your website via your web CMS.`
      }
    },

    computed: {
      ...mapGetters('squeues', { squeueOptions: 'enabledAsObject' }),
      ...mapStores(
        useCommunityqPublishingStore,
        useExportFormatsStore,
        useWebCategoriesStore,
        useStoryBatchOptionsStore,
        useSectionsStore,
        useUsersStore
      ),
      sectionOptions() { return this.sectionsStore.enabledAsObject },
      downloadLink() {
        return `/stories.zip?${jQuery.param({ids: this.storyBatchOptionsStore.selectedStoryIds})}`
      },
      hasBatchExportFormats() {
        return this.exportFormatsStore.forBatchExport.length > 0
      }
    },

    mounted() {
      const { dispatch } = this.$store;

      this.usersStore.fetchOnce();
      this.sectionsStore.fetchOnce();
      dispatch('squeues/fetchOnce');
      this.webCategoriesStore.fetchOnce();
      this.exportFormatsStore.fetchOnce();
    },

    methods: {
      buttonTitle(action) {
        switch (this.storyBatchOptionsStore.selectedStoriesCount) {
          case 1: return `${action} Story`;
          case 2: return `${action} Both Stories`;
          default: return `${action} All ${this.storyBatchOptionsStore.selectedStoriesCount} Stories`;
        }
      },

      openCommunityQPublishModal() {
        this.communityqPublishingStore.storyIds = this.storyBatchOptionsStore.selectedStoryIds
      },

      downloadBatchExport(exportFormatId) {
        const params = {
          ids: this.storyBatchOptionsStore.selectedStoryIds,
          export_format_id: exportFormatId
        }
        window.location = `/stories.batch_zip?${jQuery.param(params)}`
      },

      submit() {
        if (!this.storyBatchOptionsStore.anyChanges) {
          alert('Please select a change to at least one field');
        } else {
          const data = { ids: this.storyBatchOptionsStore.selectedStoryIds, story: this.storyBatchOptionsStore.storyData };

          httpClient.patch('/api/internal/stories/batch_update', data).then(() => {
            this.$emit('change');
          }).catch(err => {
            this.$store.dispatch('messages/addError', err);
          })
        }
      }
    }
  }
</script>
